import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedSection extends Component {
  /**
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * @type {boolean}
   */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.recentlyViewedPerformed === 'true') {
      this.#loaded = true;
      return;
    }

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  #loadRecentlyViewed() {
    if (this.#loaded) return;

    const { sectionId, excludeId } = this.dataset;
    if (!sectionId) return;

    const ids = RecentlyViewed.getProducts().filter(
      (/** @type {string} */ id) => !excludeId || id !== excludeId
    );

    if (ids.length === 0) {
      this.#hide();
      return;
    }

    const searchUrl = (window.Theme && window.Theme.routes && window.Theme.routes.search_url) || '/search';
    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', ids.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    fetch(url.toString(), { signal: this.#activeFetch.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        return response.text();
      })
      .then((markup) => {
        if (!markup.trim().length) {
          this.#hide();
          return;
        }

        const parsed = new DOMParser().parseFromString(markup, 'text/html');
        const hasItems = parsed.querySelector('[data-has-recently-viewed="true"]');

        if (!hasItems) {
          this.#hide();
          return;
        }

        this.#loaded = true;
        this.dataset.recentlyViewedPerformed = 'true';
        morphSection(sectionId, markup, { mode: 'hydration', injectStylesheet: true });
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Recently viewed error:', error.message);
        this.#hide();
      })
      .finally(() => {
        this.#activeFetch = null;
      });
  }

  #hide() {
    this.classList.add('hidden');
    this.dataset.recentlyViewedEmpty = 'true';
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewedSection);
}
